import { LoginOutlined, LogoutOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { App, Button, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthenticator, useCurrentUser } from '../auth-context';
import { SIGN_IN_PATH } from '../return-path';

/** The header's sign-in link, or the signed-in user's name and a sign-out button. */
export function AccountMenu() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const authenticator = useAuthenticator();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const onSignInPage = useRouterState({
    select: (state) => state.location.pathname === SIGN_IN_PATH,
  });
  const signOut = useMutation({
    mutationFn: async () => {
      // To the public landing page first, so the guard doesn't turn the current page into a sign-in.
      await navigate({ to: '/' });
      await authenticator.signOut();
    },
    onError: () => {
      void message.error(t('auth.menu.sign_out_failed'));
    },
  });

  if (user === null) {
    return onSignInPage ? null : (
      <Link to={SIGN_IN_PATH} style={{ whiteSpace: 'nowrap' }}>
        <Flex align="center" gap="small">
          <LoginOutlined aria-hidden />
          {t('auth.menu.sign_in')}
        </Flex>
      </Link>
    );
  }

  // Accounts without a display name show their email instead.
  const name = user.displayName ?? user.email;

  return (
    <Flex align="center" gap="small">
      {name !== null && (
        <Typography.Text type="secondary" ellipsis style={{ maxWidth: 240 }}>
          {name}
        </Typography.Text>
      )}
      <Button
        icon={<LogoutOutlined aria-hidden />}
        loading={signOut.isPending}
        onClick={() => {
          signOut.mutate();
        }}
      >
        {t('auth.menu.sign_out')}
      </Button>
    </Flex>
  );
}
